import { buildWordTool, type WordTool } from './builder';
import { VALID_STYLES, FORMATTING_KEYS, HIGHLIGHT_COLORS, preview } from './constants';

function validateFormatFields(item: Record<string, unknown>): { error: string; recovery: string } | null {
  if (item.superscript === true && item.subscript === true) {
    return {
      error: 'Cannot set both superscript and subscript to true.',
      recovery: 'Set only one of superscript or subscript to true.',
    };
  }
  if (typeof item.color === 'string') {
    const color = item.color;
    if (color.startsWith('#')) {
      return {
        error: 'color should not include the # prefix.',
        recovery: `Use "${color.slice(1)}" instead of "${color}".`,
      };
    }
    if (!/^[0-9A-Fa-f]{6}$/.test(color)) {
      return {
        error: `Invalid color format "${color}".`,
        recovery: 'Use a 6-character hex color without #. Example: "FF0000" for red.',
      };
    }
  }
  if (typeof item.size === 'number' && item.size <= 0) {
    return { error: 'Font size must be positive.', recovery: 'Use a value like 11, 12, or 14.' };
  }
  if (
    typeof item.highlight === 'string' &&
    !HIGHLIGHT_COLORS.includes(item.highlight as (typeof HIGHLIGHT_COLORS)[number])
  ) {
    return {
      error: `Invalid highlight color "${item.highlight}".`,
      recovery: `Use one of: ${HIGHLIGHT_COLORS.join(', ')}.`,
    };
  }
  return null;
}

function applyInlineFormatting(range: Word.Range, item: Record<string, unknown>) {
  if (typeof item.bold === 'boolean') range.font.bold = item.bold;
  if (typeof item.italic === 'boolean') range.font.italic = item.italic;
  if (typeof item.strikethrough === 'boolean') range.font.strikeThrough = item.strikethrough as boolean;
  if (typeof item.superscript === 'boolean') range.font.superscript = item.superscript;
  if (typeof item.subscript === 'boolean') range.font.subscript = item.subscript;

  if (typeof item.underline_style === 'string') {
    const map: Record<string, Word.UnderlineType> = {
      none: Word.UnderlineType.none,
      single: Word.UnderlineType.single,
      double: Word.UnderlineType.double,
      dotted: Word.UnderlineType.dotted,
      dashed: Word.UnderlineType.dashLine,
      wave: Word.UnderlineType.wave,
    };
    range.font.underline = map[item.underline_style] ?? Word.UnderlineType.none;
  } else if (typeof item.underline === 'boolean') {
    range.font.underline = item.underline ? Word.UnderlineType.single : Word.UnderlineType.none;
  }

  if (typeof item.color === 'string') range.font.color = item.color;
  if (typeof item.size === 'number') range.font.size = item.size;
  if (typeof item.font === 'string') range.font.name = item.font;
  if (typeof item.highlight === 'string') range.font.highlightColor = item.highlight;
}

const PARAGRAPH_FORMAT_KEYS = [
  'style',
  'alignment',
  'space_before',
  'space_after',
  'line_spacing',
  'left_indent',
  'right_indent',
  'first_line_indent',
  'keep_together',
  'keep_with_next',
  'page_break_before',
] as const;

export const formattingTools: WordTool[] = [
  buildWordTool({
    name: 'format_text',
    description:
      'Apply inline font formatting to text. ONE CALL, MANY REGIONS: prefer the `queries` array to format up to 20 different text regions with different properties in a single call — much cheaper than repeated calls. Single-target mode: use `query` (find text), `paragraph_index` (whole paragraph), or neither (current selection). For heading level / alignment / spacing, use format_paragraph. Never fake headings with bold+size.',
    parameters: {
      type: 'object',
      properties: {
        queries: {
          type: 'array',
          description:
            'Batch mode. Array of {query|paragraph_index, match_index?, ...format fields} — up to 20 items. One call instead of many. Mutually exclusive with query/paragraph_index. Example: [{"query":"Total","bold":true},{"query":"NOTE","italic":true,"color":"FF0000"}].',
        },
        query: {
          type: 'string',
          description:
            "Single-target: text to find and format (case-insensitive). Use 3+ unique words. Mutually exclusive with paragraph_index and queries.",
        },
        match_index: {
          type: 'number',
          description:
            'Which match to format when query finds multiple (0-based). Defaults to 0.',
        },
        paragraph_index: {
          type: 'number',
          description:
            'Format all text in this paragraph. Zero-based index from get_document. Mutually exclusive with query and queries.',
        },
        bold: {
          type: 'boolean',
          description:
            'Set to true for bold, false to explicitly remove bold. Omit to leave unchanged. Do NOT use bold to fake a heading — use format_paragraph with a style.',
        },
        italic: {
          type: 'boolean',
          description: 'Set to true for italic, false to remove italic. Omit to leave unchanged.',
        },
        underline: {
          type: 'boolean',
          description:
            'Set to true for single underline, false to remove. For non-single styles (double, dotted, dashed, wave), use underline_style instead.',
        },
        underline_style: {
          type: 'string',
          enum: ['none', 'single', 'double', 'dotted', 'dashed', 'wave'],
          description:
            "Specific underline style. Overrides the underline parameter if both are provided. Use 'none' to remove any underline.",
        },
        strikethrough: {
          type: 'boolean',
          description: 'Set to true for strikethrough, false to remove. Omit to leave unchanged.',
        },
        superscript: {
          type: 'boolean',
          description:
            'Set to true for superscript. Cannot be combined with subscript — setting both true will produce unexpected results.',
        },
        subscript: {
          type: 'boolean',
          description:
            'Set to true for subscript. Cannot be combined with superscript — setting both true will produce unexpected results.',
        },
        color: {
          type: 'string',
          description:
            "Font color as 6-character hex WITHOUT the # prefix. Examples: 'FF0000' (red), '0000FF' (blue), '000000' (black). Passing '#FF0000' will be rejected — omit the '#'.",
        },
        size: {
          type: 'number',
          description:
            'Font size in points. Must be > 0. Typical: 10–12 for body, 14–16 for subheadings. For heading sizes, prefer format_paragraph with a style.',
        },
        font: {
          type: 'string',
          description:
            "Font family name exactly as it appears in Word. Common: 'Calibri', 'Arial', 'Times New Roman', 'Courier New'. The font must be installed on the user's system or it will fall back silently.",
        },
        highlight: {
          type: 'string',
          enum: [
            'Yellow', 'Green', 'Turquoise', 'Pink', 'Blue', 'Red', 'DarkBlue', 'Teal',
            'Violet', 'DarkRed', 'DarkYellow', 'Gray25', 'Gray50', 'Black', 'White', 'NoColor',
          ],
          description: "Text highlight color. Use 'NoColor' to remove highlighting.",
        },
      },
    },
    permission: true,
    validateInput: (args) => {
      const hasBatch = Array.isArray(args.queries);
      const hasSingle =
        typeof args.query === 'string' || typeof args.paragraph_index === 'number';

      if (hasBatch && hasSingle) {
        return {
          error: 'Provide either queries (batch) or query/paragraph_index (single), not both.',
          recovery: 'Move single-item inputs into the queries array, or remove the array.',
        };
      }

      if (hasBatch) {
        const queries = args.queries as Array<Record<string, unknown>>;
        if (queries.length === 0) {
          return { error: 'queries array is empty.', recovery: 'Provide at least one item.' };
        }
        if (queries.length > 20) {
          return {
            error: `queries has ${queries.length} items; maximum is 20.`,
            recovery: 'Split into multiple calls of up to 20 items each.',
          };
        }
        for (let i = 0; i < queries.length; i++) {
          const q = queries[i]!;
          const perItemProvided = FORMATTING_KEYS.filter((k) => k in q);
          if (perItemProvided.length === 0) {
            return {
              error: `queries[${i}] has no formatting properties.`,
              recovery:
                'Each item must include at least one of bold/italic/underline/color/size/font/highlight/...',
            };
          }
          if (typeof q.query !== 'string' && typeof q.paragraph_index !== 'number') {
            return {
              error: `queries[${i}] must provide query or paragraph_index.`,
              recovery: 'Add a query string or paragraph_index to target the text.',
            };
          }
          if (typeof q.query === 'string' && typeof q.paragraph_index === 'number') {
            return {
              error: `queries[${i}] has both query and paragraph_index.`,
              recovery: 'Use one target per item.',
            };
          }
          const itemErr = validateFormatFields(q);
          if (itemErr) return { error: `queries[${i}]: ${itemErr.error}`, recovery: itemErr.recovery };
        }
        return null;
      }

      const provided = FORMATTING_KEYS.filter((k) => k in args);
      if (provided.length === 0) {
        return {
          error: 'No formatting properties provided.',
          recovery:
            'Include at least one of: bold, italic, underline, color, size, font, highlight, strikethrough, superscript, subscript, underline_style.',
        };
      }
      const err = validateFormatFields(args);
      if (err) return err;
      if (typeof args.query === 'string' && typeof args.paragraph_index === 'number') {
        return {
          error: 'Provide either query or paragraph_index, not both.',
          recovery: 'Use query to find text by content, or paragraph_index to target a whole paragraph.',
        };
      }
      return null;
    },
    getActivityDescription: (args) => {
      if (Array.isArray(args.queries)) return `Formatting ${args.queries.length} regions`;
      if (typeof args.query === 'string') {
        const q = args.query.length > 40 ? args.query.slice(0, 40) + '…' : args.query;
        return `Formatting "${q}"`;
      }
      if (typeof args.paragraph_index === 'number') return `Formatting paragraph ${args.paragraph_index}`;
      return 'Formatting selection';
    },
    execute: async (args) => {
      const isBatch = Array.isArray(args.queries);
      return await Word.run(async (context) => {
        const items: Array<Record<string, unknown>> = isBatch
          ? (args.queries as Array<Record<string, unknown>>)
          : [args];

        // Pass 1: queue EVERY search / paragraph-index lookup behind a single sync.
        type Pending = {
          item: Record<string, unknown>;
          kind: 'query' | 'paragraph' | 'selection';
          searchResults?: Word.RangeCollection;
          paragraphsCollection?: Word.ParagraphCollection;
          range?: Word.Range;
        };
        const pending: Pending[] = items.map((item) => {
          if (typeof item.query === 'string') {
            const sr = context.document.body.search(item.query, { matchCase: false });
            sr.load('items');
            return { item, kind: 'query', searchResults: sr };
          }
          if (typeof item.paragraph_index === 'number') {
            const paragraphs = context.document.body.paragraphs;
            paragraphs.load('items');
            return { item, kind: 'paragraph', paragraphsCollection: paragraphs };
          }
          return { item, kind: 'selection', range: context.document.getSelection() };
        });
        await context.sync();

        // Pass 2: resolve the concrete Range for each, apply formatting, queue text loads.
        const resolved: Array<
          | {
              range: Word.Range;
              item: Record<string, unknown>;
              totalMatches?: number;
              matchIdx?: number;
            }
          | { error: string; recovery: string; item: Record<string, unknown> }
        > = [];

        for (const p of pending) {
          if (p.kind === 'query') {
            const results = p.searchResults!;
            if (results.items.length === 0) {
              resolved.push({
                item: p.item,
                error: `Text "${preview(p.item.query as string, 60)}" not found.`,
                recovery: 'Use get_document to check exact text.',
              });
              continue;
            }
            const mi = typeof p.item.match_index === 'number' ? p.item.match_index : 0;
            if (mi < 0 || mi >= results.items.length) {
              resolved.push({
                item: p.item,
                error: `match_index ${mi} out of range (${results.items.length} matches).`,
                recovery: `Use 0 to ${results.items.length - 1}.`,
              });
              continue;
            }
            resolved.push({
              range: results.items[mi]!,
              item: p.item,
              totalMatches: results.items.length,
              matchIdx: mi,
            });
          } else if (p.kind === 'paragraph') {
            const paragraphs = p.paragraphsCollection!;
            const idx = p.item.paragraph_index as number;
            const para = paragraphs.items[idx];
            if (!para) {
              resolved.push({
                item: p.item,
                error: `paragraph_index ${idx} out of range (${paragraphs.items.length} paragraphs).`,
                recovery: `Use 0 to ${paragraphs.items.length - 1}. Call get_document for valid indices.`,
              });
              continue;
            }
            resolved.push({ range: para.getRange(), item: p.item });
          } else {
            resolved.push({ range: p.range!, item: p.item });
          }
        }

        for (const r of resolved) {
          if ('error' in r) continue;
          r.range.load('text');
          applyInlineFormatting(r.range, r.item);
        }
        await context.sync();

        const results: Array<Record<string, unknown>> = resolved.map((r, i) => {
          if ('error' in r) return { index: i, error: r.error, recovery: r.recovery };
          const applied: Record<string, unknown> = {};
          for (const key of FORMATTING_KEYS) if (key in r.item) applied[key] = r.item[key];
          const base: Record<string, unknown> = {
            success: true,
            formatted_text: preview(r.range.text, 60),
            formatting_applied: applied,
            char_count: r.range.text.length,
          };
          if (r.totalMatches !== undefined) {
            base.total_matches = r.totalMatches;
            base.match_index = r.matchIdx;
          }
          return base;
        });

        if (isBatch) {
          const successes = results.filter((r) => r.success).length;
          return JSON.stringify({
            success: successes === results.length,
            formatted: successes,
            total: results.length,
            results,
          });
        }

        return JSON.stringify(results[0]!);
      });
    },
  }),

  buildWordTool({
    name: 'format_paragraph',
    description:
      "Paragraph-level formatting: style (headings), alignment, spacing, indentation, page-break control. Use paragraph_indices to format many paragraphs in one call. For inline font formatting (bold, italic, color) use format_text. Only provided properties change. Tip: if you're about to call this right after insert_content, pass the same properties directly to insert_content instead — it accepts them.",
    parameters: {
      type: 'object',
      properties: {
        paragraph_index: {
          type: 'number',
          description:
            "Zero-based index of a single paragraph to format. Mutually exclusive with paragraph_indices. Get from get_document.",
        },
        paragraph_indices: {
          type: 'array',
          description:
            'Format multiple paragraphs in one call. Array of zero-based indices. Mutually exclusive with paragraph_index. Example: [0, 3, 7].',
        },
        style: {
          type: 'string',
          description:
            "Built-in Word style. This is the correct way to set heading level. Valid: Normal, Heading1, Heading2, Heading3, Heading4, Title, Subtitle, Quote, IntenseQuote, ListParagraph, NoSpacing.",
        },
        alignment: {
          type: 'string',
          enum: ['left', 'center', 'right', 'justified'],
          description: 'Horizontal text alignment within the paragraph.',
        },
        space_before: {
          type: 'number',
          description: 'Space above the paragraph in points (must be >= 0). 12pt is roughly one line.',
        },
        space_after: {
          type: 'number',
          description:
            'Space below the paragraph in points (must be >= 0). Word defaults vary by style (~8-10pt for Normal).',
        },
        line_spacing: {
          type: 'number',
          description:
            'Line spacing in points (must be > 0). Single at 12pt body = 12pt. 1.5x = 18pt. Double = 24pt.',
        },
        left_indent: {
          type: 'number',
          description: 'Left indent in points (72pt = 1 inch). Use for block quotes or nested content.',
        },
        right_indent: { type: 'number', description: 'Right indent in points (72pt = 1 inch).' },
        first_line_indent: {
          type: 'number',
          description:
            'First-line indent in points. Positive = standard indent. Negative = hanging indent (common in bibliographies).',
        },
        keep_together: {
          type: 'boolean',
          description: 'Keep all lines of this paragraph on the same page.',
        },
        keep_with_next: {
          type: 'boolean',
          description:
            'Keep this paragraph on the same page as the next. Use on headings to prevent orphaning at page bottom.',
        },
        page_break_before: {
          type: 'boolean',
          description:
            'True forces this paragraph to start on a new page; false removes. Preferred over insert_break for structural page control.',
        },
      },
      required: [],
    },
    permission: true,
    validateInput: (args) => {
      const hasSingle = typeof args.paragraph_index === 'number';
      const hasBatch = Array.isArray(args.paragraph_indices);

      if (hasSingle && hasBatch) {
        return {
          error: 'Provide either paragraph_index (single) or paragraph_indices (batch), not both.',
          recovery: 'Use paragraph_index for one paragraph, or paragraph_indices for multiple.',
        };
      }
      if (!hasSingle && !hasBatch) {
        return {
          error: 'No paragraph targeted.',
          recovery: 'Provide paragraph_index (single) or paragraph_indices (array) from get_document.',
        };
      }
      const hasFormatting = PARAGRAPH_FORMAT_KEYS.some((k) => k in args);
      if (!hasFormatting) {
        return {
          error: 'No formatting properties provided.',
          recovery: `Include at least one of: ${PARAGRAPH_FORMAT_KEYS.join(', ')}.`,
        };
      }
      if (
        typeof args.style === 'string' &&
        !VALID_STYLES.includes(args.style as (typeof VALID_STYLES)[number])
      ) {
        return {
          error: `Unknown style "${args.style}".`,
          recovery: `Use one of: ${VALID_STYLES.join(', ')}.`,
        };
      }
      if (typeof args.space_before === 'number' && args.space_before < 0) {
        return { error: 'space_before must be >= 0.', recovery: 'Provide a non-negative value in points.' };
      }
      if (typeof args.space_after === 'number' && args.space_after < 0) {
        return { error: 'space_after must be >= 0.', recovery: 'Provide a non-negative value in points.' };
      }
      if (typeof args.line_spacing === 'number' && args.line_spacing <= 0) {
        return { error: 'line_spacing must be > 0.', recovery: 'Provide a positive value in points.' };
      }
      if (hasSingle) {
        const idx = args.paragraph_index as number;
        if (!Number.isInteger(idx) || idx < 0) {
          return {
            error: 'paragraph_index must be a non-negative integer.',
            recovery: 'Call get_document to see valid paragraph indices (0-based).',
          };
        }
      }
      return null;
    },
    getActivityDescription: (args) => {
      if (Array.isArray(args.paragraph_indices)) {
        return `Formatting ${args.paragraph_indices.length} paragraphs`;
      }
      if (typeof args.paragraph_index === 'number') return `Formatting paragraph ${args.paragraph_index}`;
      return 'Formatting paragraphs';
    },
    execute: async (args) => {
      const hasBatch = Array.isArray(args.paragraph_indices);
      const indices: number[] = hasBatch
        ? (args.paragraph_indices as number[])
        : [args.paragraph_index as number];

      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const results: Array<Record<string, unknown>> = [];

        for (const idx of indices) {
          const p = paragraphs.items[idx];
          if (!p) {
            results.push({
              index: idx,
              error: `Index ${idx} out of range (${paragraphs.items.length} paragraphs)`,
            });
            continue;
          }

          if (args.style) p.styleBuiltIn = args.style as Word.BuiltInStyleName;
          if (args.alignment) {
            const alignMap: Record<string, Word.Alignment> = {
              left: Word.Alignment.left,
              center: Word.Alignment.centered,
              right: Word.Alignment.right,
              justified: Word.Alignment.justified,
            };
            p.alignment = alignMap[args.alignment as string]!;
          }
          if (typeof args.space_before === 'number') p.spaceBefore = args.space_before;
          if (typeof args.space_after === 'number') p.spaceAfter = args.space_after;
          if (typeof args.line_spacing === 'number') p.lineSpacing = args.line_spacing;
          if (typeof args.left_indent === 'number') p.leftIndent = args.left_indent;
          if (typeof args.right_indent === 'number') p.rightIndent = args.right_indent;
          if (typeof args.first_line_indent === 'number') p.firstLineIndent = args.first_line_indent;
          // keepTogether, keepWithNext, and pageBreakBefore are not in all API sets.
          // Guard each individually so one missing property does not abort the batch.
          if (typeof args.keep_together === 'boolean') {
            try {
              (p as any).keepTogether = args.keep_together;
            } catch {
              /* unavailable in this API set */
            }
          }
          if (typeof args.keep_with_next === 'boolean') {
            try {
              (p as any).keepWithNext = args.keep_with_next;
            } catch {
              /* unavailable in this API set */
            }
          }
          if (typeof args.page_break_before === 'boolean') {
            try {
              (p as any).pageBreakBefore = args.page_break_before;
            } catch {
              /* unavailable in this API set */
            }
          }

          results.push({ index: idx, success: true });
        }

        await context.sync();

        for (const r of results) {
          if (r.success && typeof r.index === 'number') {
            const p = paragraphs.items[r.index as number];
            if (p) p.load('text,styleBuiltIn');
          }
        }
        await context.sync();

        for (const r of results) {
          if (r.success && typeof r.index === 'number') {
            const p = paragraphs.items[r.index as number];
            if (p) {
              r.text = preview(p.text);
              r.resulting_style = p.styleBuiltIn;
            }
          }
        }

        const applied: Record<string, unknown> = {};
        for (const key of PARAGRAPH_FORMAT_KEYS) {
          if (key in args) applied[key] = args[key];
        }

        if (hasBatch) {
          return JSON.stringify({
            success: true,
            paragraphs_formatted: results.filter((r) => r.success).length,
            results,
            formatting_applied: applied,
          });
        }

        const single = results[0]!;
        if (single.error) {
          return JSON.stringify({
            error: `Paragraph index ${indices[0]} out of range (${paragraphs.items.length} paragraphs).`,
            recovery: `Use an index from 0 to ${paragraphs.items.length - 1}. Call get_document to see valid indices.`,
          });
        }
        return JSON.stringify({
          success: true,
          paragraph_index: indices[0],
          paragraph_text: single.text,
          resulting_style: single.resulting_style,
          formatting_applied: applied,
        });
      });
    },
  }),

  buildWordTool({
    name: 'manage_list',
    description:
      "Create or remove bulleted/numbered lists. PREREQUISITE: Call get_document to get paragraph indices. For 'add': the first paragraph starts a new list, remaining paragraphs join it. Paragraphs should be contiguous for a well-formed list. For 'remove': detaches paragraphs from their list without deleting text. For deleting list text entirely, use delete_content. For changing paragraph indent without list formatting, use format_paragraph with left_indent. Returns action taken and paragraphs affected.",
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          enum: ['add', 'remove'],
          description: "'add' creates a list from the given paragraphs; 'remove' detaches them from their list.",
        },
        paragraph_indices: {
          type: 'array',
          description:
            'Zero-based paragraph indices to affect. Should be contiguous for a well-formed list. Get from get_document. Example: [2, 3, 4].',
        },
        list_type: {
          type: 'string',
          enum: ['bullet', 'number'],
          description: "Required for 'add'. 'bullet' for unordered, 'number' for ordered. Ignored for 'remove'.",
        },
        level: {
          type: 'number',
          description:
            'Nesting level 0-8. 0 = top level (default), 1 = first indent, etc. Only meaningful for add.',
        },
        left_indent: {
          type: 'number',
          description:
            'Left indent in points (72pt = 1 inch) applied to every affected paragraph. Fused in same call — avoids follow-up format_paragraph.',
        },
        right_indent: {
          type: 'number',
          description: 'Right indent in points applied to every affected paragraph.',
        },
      },
      required: ['action', 'paragraph_indices'],
    },
    permission: true,
    validateInput: (args) => {
      const action = args.action as string;
      const indices = args.paragraph_indices as number[] | undefined;

      if (!indices || indices.length === 0) {
        return {
          error: 'paragraph_indices is empty.',
          recovery: 'Provide at least one paragraph index from get_document.',
        };
      }
      if (action === 'add' && typeof args.list_type !== 'string') {
        return {
          error: "list_type ('bullet' or 'number') is required when action is 'add'.",
          recovery: "Add list_type: 'bullet' for unordered or 'number' for ordered.",
        };
      }
      const level = typeof args.level === 'number' ? args.level : 0;
      if (level < 0 || level > 8) {
        return {
          error: `level ${level} out of range.`,
          recovery: 'Use a value from 0 (top level) to 8 (deepest nesting).',
        };
      }
      return null;
    },
    getActivityDescription: (args) =>
      args.action === 'add'
        ? `Adding ${args.list_type ?? ''} list (${Array.isArray(args.paragraph_indices) ? args.paragraph_indices.length : 0} items)`
        : `Removing list from ${Array.isArray(args.paragraph_indices) ? args.paragraph_indices.length : 0} paragraphs`,
    execute: async (args) => {
      const action = args.action as string;
      const indices = args.paragraph_indices as number[];
      const level = typeof args.level === 'number' ? args.level : 0;

      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        for (const idx of indices) {
          if (idx < 0 || idx >= paragraphs.items.length) {
            return JSON.stringify({
              error: `paragraph_indices contains ${idx}, which is out of range (${paragraphs.items.length} paragraphs).`,
              recovery: `Use indices from 0 to ${paragraphs.items.length - 1}. Call get_document to check.`,
            });
          }
        }

        if (action === 'add') {
          const firstP = paragraphs.items[indices[0]!]!;
          const list = firstP.startNewList();
          await context.sync();

          list.load('id');
          await context.sync();

          if (args.list_type === 'bullet') {
            list.setLevelBullet(level, Word.ListBullet.solid);
          } else {
            list.setLevelNumbering(level, Word.ListNumbering.arabic);
          }

          for (let i = 1; i < indices.length; i++) {
            const p = paragraphs.items[indices[i]!]!;
            p.attachToList(list.id, level);
          }
          await context.sync();
        } else {
          for (const idx of indices) {
            paragraphs.items[idx]!.detachFromList();
          }
          await context.sync();
        }

        if (typeof args.left_indent === 'number' || typeof args.right_indent === 'number') {
          for (const idx of indices) {
            const p = paragraphs.items[idx]!;
            if (typeof args.left_indent === 'number') p.leftIndent = args.left_indent;
            if (typeof args.right_indent === 'number') p.rightIndent = args.right_indent;
          }
          await context.sync();
        }

        return JSON.stringify({
          success: true,
          action,
          list_type: action === 'add' ? args.list_type : undefined,
          paragraphs_affected: indices.length,
          level: action === 'add' ? level : undefined,
          left_indent: typeof args.left_indent === 'number' ? args.left_indent : undefined,
          right_indent: typeof args.right_indent === 'number' ? args.right_indent : undefined,
        });
      });
    },
  }),
];
